import { zodResolver } from '@hookform/resolvers/zod';
import {
  BucketSchema,
  BucketTypeEnum,
  IBucketResponseSchema,
  IBucketSchema,
  IBucketUpdatePayloadSchema,
} from '@monetize/types/app';
import pick from 'lodash/pick';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdLock } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  useCreateEntity,
  useGetById,
  useUpdateEntity,
} from '../../../api/queryUtils';
import {
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MInput,
  MPageHeader,
  MPageLoader,
  MStack,
  MText,
  MTooltip,
  MVStack,
} from '../../../components/Monetize';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { BUCKETS, ROUTES } from '../../../constants';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import { logFormError } from '../../../services/logger';
import { getHeaderTitle } from '../../../utils/productCatalog';

interface BucketFormProps {
  isOpen: boolean;
  bucketId?: string;
  onClose: (id?: string) => void;
}

export const BucketForm = ({ isOpen, bucketId, onClose }: BucketFormProps) => {
  const navigate = useNavigate();
  const { isReadOnly, inputVariant } = useSettingsReadOnlyRule();

  const {
    isLoading: isBucketLoading,
    data: bucket,
    isError,
  } = useGetById<IBucketResponseSchema>('productCatalogBuckets', bucketId!, {
    enabled: !!bucketId,
    refetchOnWindowFocus: false,
    meta: { showErrorToast: true },
  });

  const { mutate: doCreateBucket, isPending: isCreateBucketLoading } =
    useCreateEntity<IBucketResponseSchema, IBucketSchema>(
      'productCatalogBuckets',
      {
        onSuccess: (response) => onCloseHandle(response?.id),
        meta: { showErrorToast: true },
      },
    );

  const { mutate: doUpdateBucket, isPending: isUpdateBucketLoading } =
    useUpdateEntity<IBucketResponseSchema, IBucketUpdatePayloadSchema>(
      'productCatalogBuckets',
      {
        onSuccess: (response) => onCloseHandle(response?.id),
        meta: { showErrorToast: true },
      },
    );

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<IBucketSchema>({
    resolver: zodResolver(BucketSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      type: BucketTypeEnum.TOKEN,
    },
  });

  useEffect(() => {
    if (bucket) {
      const formData = pick(bucket, Object.keys(BucketSchema.shape));

      reset({
        ...formData,
      });
    } else if (isError) {
      navigate(ROUTES.BUCKET_LIST);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, isError]);

  const isBucketLocked = !!bucket?.locked;

  const isDataLoading = bucketId && isBucketLoading;

  const isSaveLoading =
    isDataLoading || isCreateBucketLoading || isUpdateBucketLoading;

  const onSubmit = async (data: IBucketSchema) => {
    if (!bucketId) {
      doCreateBucket(data);
    } else {
      doUpdateBucket({
        id: bucketId,
        payload: { id: bucketId, ...data },
      });
    }
  };

  const onCloseHandle = (id?: string) => {
    reset();
    onClose(id);
  };

  const lockedIcon = (
    <MFlex>
      <MTooltip
        contentProps={{ mt: -1, ml: 10 }}
        label="Some of the information in this bucket cannot be edited
because this bucket is currently being used."
        placement="bottom-start"
      >
        <span tabIndex={-1}>
          <MIcon
            w={4}
            h={6}
            ml="3"
            as={MdLock}
            fontSize="xl"
            alignSelf="center"
            style={{
              cursor: 'pointer',
            }}
          />
        </span>
      </MTooltip>
    </MFlex>
  );

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onCloseHandle}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderModalTitleActions={() =>
        !isDataLoading && (
          <MVStack alignItems="flex-start">
            <MPageHeader
              title={getHeaderTitle({
                isReadOnly,
                id: bucketId,
                title: 'Bucket',
              })}
              id={bucketId}
              copyUrl
              tag={isBucketLocked ? lockedIcon : ''}
              containerProps={{ mb: 0 }}
            ></MPageHeader>
          </MVStack>
        )
      }
      renderFooter={() => {
        if (isReadOnly) {
          return null;
        }
        return (
          <MStack
            gap={5}
            direction="row"
            align="center"
            justifyContent="end"
            flex={1}
          >
            <MButton
              onClick={() => onCloseHandle()}
              variant="cancel"
              minW="auto"
            >
              {isReadOnly ? 'Close' : 'Cancel'}
            </MButton>
            {!isReadOnly && (
              <MButton
                variant="primary"
                isLoading={isSaveLoading}
                isDisabled={isSaveLoading || !isValid || !isDirty}
                minW="auto"
                type="submit"
                form="bucket-form"
              >
                Save
              </MButton>
            )}
          </MStack>
        );
      }}
      modalFooterProps={isReadOnly ? { border: 'none', pb: 0 } : undefined}
    >
      <MText mb={4}>
        A bucket tracks tokens/credits, or a prepaid cash balance. During
        invoicing, consumption-based products deduct from this balance
        automatically.
      </MText>
      {isDataLoading ? (
        <MFlex justify="center" grow={1} height={'100%'}>
          <MPageLoader height="auto" />
        </MFlex>
      ) : (
        <MBox width="100%">
          <form
            id="bucket-form"
            onSubmit={handleSubmit(onSubmit, logFormError)}
            style={{ maxWidth: '725px', margin: '0 auto' }}
          >
            <MGrid templateColumns="repeat(12, 1fr)" rowGap={4} columnGap={8}>
              <MGridItem colSpan={12}>
                <MFormField error={errors.name} label="Name" isRequired>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <MInput
                        {...field}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        placeholder="Enter Bucket Name"
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={12}>
                <MFormField
                  error={errors.type}
                  label="Type"
                  isRequired
                  tooltip={
                    isBucketLocked
                      ? 'Type cannot be changed for a locked bucket.'
                      : 'Choose Token to track units, or Cash to track a prepaid balance.'
                  }
                >
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <MCustomSelect
                        placeholder="Select Bucket Type"
                        items={BUCKETS.BucketStatusSelectOptions}
                        isDisabled={isSaveLoading || isBucketLocked}
                        isReadOnly={isReadOnly || isBucketLocked}
                        variant={inputVariant}
                        {...field}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
            </MGrid>
          </form>
        </MBox>
      )}
    </MCenterModal>
  );
};
